import type { FluxErrorEntityErrorCodes } from '../types.js';

export const PipeErrorCodes = {
  PipeAlreadyHasDeadline: {
    code: 'pipe/already-has-deadline',
    message: 'Este fluxo já possui um prazo definido.',
    explanation: 'Este fluxo (pipe) já possui um prazo definido.',
    localizations: {},
  },
  PipeCannotRemoveAssignedMembers: {
    code: 'pipe/cannot-remove-assigned-members',
    message: 'Não é possível remover membros que estão atribuídos a uma automação.',
    explanation:
      'Você não pode remover membros de um fluxo (pipe) que estão atribuídos a uma automação.',
    localizations: {},
  },
  PipeCreate: {
    code: 'pipe/create',
    message: 'Erro ao criar este fluxo.',
    explanation:
      'Ocorreu um erro desconhecido ao tentar criar um novo fluxo (pipe). Verifique suas informações e tente novamente.',
    localizations: {},
  },
  PipeDelete: {
    code: 'pipe/delete',
    message: 'Erro ao excluir este fluxo. Tente novamente.',
    explanation: 'Ocorreu um erro ao tentar excluir este fluxo (pipe).',
    localizations: {},
  },
  PipeDoesNotHaveDeadline: {
    code: 'pipe/does-not-have-deadline',
    message: 'Este fluxo não possui um prazo definido.',
    explanation: 'Este fluxo (pipe) não possui um prazo definido.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Configuração do Fluxo',
        path: '/wiki/docs/pipe/configuration#prazos',
      },
    ],
  },
  PipeMemberNotFound: {
    code: 'pipe/member-not-found',
    message: 'Membro do fluxo não encontrado.',
    explanation: 'O membro do fluxo (pipe) que você está tentando acessar não foi encontrado.',
    localizations: {},
  },
  PipeNoStartForm: {
    code: 'pipe/no-start-form',
    message: 'Este fluxo não possui um formulário inicial configurado.',
    explanation:
      'Este fluxo (pipe) não possui um formulário inicial configurado. O formulário inicial é necessário para criar cards neste fluxo.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Configuração do Fluxo',
        path: '/wiki/docs/pipe/configuration#formulário-inicial',
      },
      {
        name: 'Formulários',
        path: '/wiki/docs/pipe/forms',
      },
    ],
  },
  PipeNotFound: {
    code: 'pipe/not-found',
    message: 'O fluxo solicitado não foi encontrado. Ele pode ter sido arquivado ou removido.',
    explanation:
      'O fluxo (pipe) que você está tentando acessar não foi encontrado. Isso pode ocorrer se o fluxo não existir ou se você não tiver permissão para acessá-lo.',
    localizations: {},
  },
  PipeNotStartForm: {
    code: 'pipe/not-start-form',
    message: 'Este fluxo não tem um formulário inicial configurado.',
    explanation: 'Este fluxo (pipe) não possui um formulário inicial configurado.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Configuração do Fluxo',
        path: '/wiki/docs/pipe/configuration#formulário-inicial',
      },
      {
        name: 'Formulários',
        path: '/wiki/docs/pipe/forms',
      },
    ],
  },
  PipeUndelete: {
    code: 'pipe/undelete',
    message: 'Erro ao restaurar este fluxo.',
    explanation: 'Ocorreu um erro ao tentar restaurar este fluxo (pipe).',
    localizations: {},
  },
  PipeUpdate: {
    code: 'pipe/update',
    message: 'Erro ao atualizar este fluxo.',
    explanation:
      'Ocorreu um erro ao tentar atualizar este fluxo (pipe). Verifique suas informações e tente novamente.',
    localizations: {},
  },
  PipeUserNotMember: {
    code: 'pipe/user-not-member',
    message: 'Você não tem acesso a este fluxo.',
    explanation: 'O usuário que você está tentando acessar não é membro deste fluxo (pipe).',
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'pipe'>;

export const PipeIdLookupErrorCodes = {
  PipeIdLookupAttachmentId: {
    code: 'pipe-id-lookup/attachment-id',
    message: 'O ID do anexo não foi encontrado.',
    explanation:
      'O ID do anexo fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o anexo foi removido ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupAutomationId: {
    code: 'pipe-id-lookup/automation-id',
    message: 'O ID da automação não foi encontrado.',
    explanation:
      'O ID da automação fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a automação foi removida ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupCardCommentId: {
    code: 'pipe-id-lookup/card-comment-id',
    message: 'O ID do comentário do card não foi encontrado.',
    explanation:
      'O ID do comentário do card fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o comentário foi removido ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupCardId: {
    code: 'pipe-id-lookup/card-id',
    message: 'O ID do card não foi encontrado.',
    explanation:
      'O ID do card fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o card foi removido ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupCardTaskAssigneeId: {
    code: 'pipe-id-lookup/card-task-assignee-id',
    message: 'O ID do responsável pela tarefa não foi encontrado.',
    explanation:
      'O ID do responsável pela tarefa do card fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o responsável pela tarefa foi removido ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupCardTaskFieldId: {
    code: 'pipe-id-lookup/card-task-field-id',
    message: 'O ID do campo da tarefa do card não foi encontrado.',
    explanation:
      'O ID do campo da tarefa do card fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o campo foi removido ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupCardTaskId: {
    code: 'pipe-id-lookup/card-task-id',
    message: 'O ID da tarefa do card não foi encontrado.',
    explanation:
      'O ID da tarefa do card fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a tarefa foi removida ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupFormAnswerQuestionId: {
    code: 'pipe-id-lookup/form-answer-question-id',
    message: 'O ID da pergunta da resposta do formulário não foi encontrado.',
    explanation:
      'O ID da pergunta da resposta do formulário fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a pergunta foi removida ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupFormId: {
    code: 'pipe-id-lookup/form-id',
    message: 'O ID do formulário não foi encontrado.',
    explanation:
      'O ID do formulário fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o formulário foi removido ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupFormQuestionId: {
    code: 'pipe-id-lookup/form-question-id',
    message: 'O ID da pergunta do formulário não foi encontrado.',
    explanation:
      'O ID da pergunta do formulário fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a pergunta foi removida ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupLabelId: {
    code: 'pipe-id-lookup/label-id',
    message: 'O ID da etiqueta não foi encontrado.',
    explanation:
      'O ID da etiqueta fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a etiqueta foi removida ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupReviewId: {
    code: 'pipe-id-lookup/review-id',
    message: 'ID da aprovação é obrigatório.',
    explanation:
      'O ID da revisão é obrigatório para a pesquisa de ID de fluxo (pipe). Esse erro pode ocorrer se a revisão foi removida ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupReviewerFieldId: {
    code: 'pipe-id-lookup/reviewer-field-id',
    message: 'O ID do campo do revisor não foi encontrado.',
    explanation:
      'O ID do campo do revisor fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o campo foi removido ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupRoleId: {
    code: 'pipe-id-lookup/role-id',
    message: 'O ID do cargo não foi encontrado.',
    explanation:
      'O ID do papel (role) fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se o papel foi removido ou se o ID está incorreto.',
    localizations: {},
  },
  PipeIdLookupStageId: {
    code: 'pipe-id-lookup/stage-id',
    message: 'O ID da etapa não foi encontrado.',
    explanation:
      'O ID da etapa fornecido para a pesquisa de ID de fluxo (pipe) não foi encontrado. Esse erro pode ocorrer se a etapa foi removida ou se o ID está incorreto.',
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'pipe-id-lookup'>;
